import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MapPin, Navigation, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/atoms/input";
import { Button } from "@/components/atoms/button";
import { toastHandler } from "@/lib/ToastHandler";
import { GOOGLE_MAPS_OPTIONS } from "@/lib/googleMapsConfig";

interface LocationPickerProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number) => void;
  onAddressChange?: (address: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: {
    lat?: string;
    lng?: string;
    address?: string;
  };
  mapHeight?: string;
  showAddressSearch?: boolean;
  addressValue?: string;
}

// Santa Tecla, El Salvador: 13.6731, -89.2797
const defaultCenter = { lat: 13.6731, lng: -89.2797 }; // Santa Tecla, El Salvador
const containerStyle = { width: "100%", height: "100%" };

const LocationPickerContent: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
  onAddressChange,
  className = "",
  disabled = false,
  placeholder = {
    lat: "13.6731",
    lng: "-89.2797",
    address: "Buscar dirección...",
  },
  mapHeight = "300px",
  showAddressSearch = true,
  addressValue = "",
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [latInput, setLatInput] = useState(latitude.toString());
  const [lngInput, setLngInput] = useState(longitude.toString());
  const [addressInput, setAddressInput] = useState(addressValue);
  const [isValidLat, setIsValidLat] = useState(true);
  const [isValidLng, setIsValidLng] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const lastToastRef = useRef(0);
  const isUpdatingRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  // Initialize center based on provided coordinates or default
  const center = React.useMemo(() => {
    if (latitude !== 0 && longitude !== 0) {
      return { lat: latitude, lng: longitude };
    }
    return defaultCenter;
  }, [latitude, longitude]);

  // Helper function to show toast with rate limiting
  const showToast = useCallback(
    (
      title: string,
      description: string,
      variant: "success" | "destructive" | "warning" = "success",
    ) => {
      const now = Date.now();
      if (now - lastToastRef.current > 3000) {
        // 3 seconds between toasts
        lastToastRef.current = now;
        toastHandler({
          title,
          description,
          variant,
        });
      }
    },
    [],
  );

  // Initialize Google Maps services
  useEffect(() => {
    if (window.google && window.google.maps && !geocoder) {
      setGeocoder(new window.google.maps.Geocoder());
    }
  }, [geocoder]);

  // Initialize Autocomplete (only once)
  useEffect(() => {
    if (
      addressInputRef.current &&
      window.google?.maps?.places &&
      !autocompleteRef.current
    ) {
      const autocompleteInstance = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          types: ["address"],
          componentRestrictions: { country: ["SV", "GT"] },
          fields: ["formatted_address", "geometry", "place_id"],
        },
      );

      // Style the autocomplete dropdown to ensure it's clickable
      setTimeout(() => {
        const dropdownElements = document.querySelectorAll(".pac-container");
        dropdownElements.forEach((container) => {
          const element = container as HTMLElement;
          element.style.zIndex = "10000";
          element.style.pointerEvents = "auto";

          // Ensure all suggestion items are clickable
          const items = element.querySelectorAll(".pac-item");
          items.forEach((item) => {
            const itemElement = item as HTMLElement;
            itemElement.style.pointerEvents = "auto";
            itemElement.style.cursor = "pointer";
          });
        });
      }, 100);

      autocompleteInstance.addListener("place_changed", () => {
        const place = autocompleteInstance.getPlace();

        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          // Set updating flag to prevent useEffect from resetting inputs
          isUpdatingRef.current = true;
          setLatInput(lat.toFixed(6));
          setLngInput(lng.toFixed(6));
          setAddressInput(place.formatted_address || "");
          setIsValidLat(true);
          setIsValidLng(true);

          onLocationChange(lat, lng);
          if (onAddressChange) {
            onAddressChange(place.formatted_address || "");
          }

          // Use a timeout to ensure map and marker are available
          setTimeout(() => {
            const currentMap = mapRef.current || map;
            const currentMarker = markerRef.current || marker;

            if (currentMarker && currentMap) {
              const position = { lat, lng };
              currentMarker.setPosition(position);
              currentMap.panTo(position);

              // Ensure appropriate zoom level for visibility
              setTimeout(() => {
                const currentZoom = currentMap.getZoom() || 0;
                if (currentZoom < 15) {
                  currentMap.setZoom(15);
                }
              }, 300);
            }
          }, 100);

          showToast(
            "Ubicación encontrada",
            "Se ha actualizado la ubicación en el mapa.",
          );

          // Reset updating flag after a longer delay to ensure all updates are complete
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 1000);
        }
      });

      // Add additional event listeners to handle dropdown interaction
      autocompleteInstance.addListener("predictions_ready", () => {
        setTimeout(() => {
          const dropdownElements = document.querySelectorAll(".pac-container");
          dropdownElements.forEach((container) => {
            const element = container as HTMLElement;
            element.style.zIndex = "10000";
            element.style.pointerEvents = "auto";
            element.style.position = "absolute";

            // Ensure all suggestion items are clickable
            const items = element.querySelectorAll(".pac-item");
            items.forEach((item) => {
              const itemElement = item as HTMLElement;
              itemElement.style.pointerEvents = "auto";
              itemElement.style.cursor = "pointer";
              itemElement.style.userSelect = "none";

              // Add click event listener as backup
              itemElement.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Trigger the autocomplete selection
                google.maps.event.trigger(
                  autocompleteInstance,
                  "place_changed",
                );
              });

              // Add mousedown event to ensure selection works
              itemElement.addEventListener("mousedown", (e) => {
                e.preventDefault();
              });
            });
          });
        }, 50);
      });

      autocompleteRef.current = autocompleteInstance;
    }
  }, [onLocationChange, onAddressChange, showToast]);

  // Additional effect to monitor and fix autocomplete dropdown styling
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const dropdownElements = document.querySelectorAll(".pac-container");
      dropdownElements.forEach((container) => {
        const element = container as HTMLElement;
        if (element.style.zIndex !== "10000") {
          element.style.zIndex = "10000";
          element.style.pointerEvents = "auto";
          element.style.position = "absolute";

          const items = element.querySelectorAll(".pac-item");
          items.forEach((item) => {
            const itemElement = item as HTMLElement;
            itemElement.style.pointerEvents = "auto";
            itemElement.style.cursor = "pointer";
            itemElement.style.userSelect = "none";
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // Disable debounced address search for now - it's causing conflicts
  // We'll rely on autocomplete and manual search instead

  // Validate coordinate format
  const validateCoordinate = useCallback(
    (value: string, type: "lat" | "lng") => {
      const num = parseFloat(value);
      if (isNaN(num)) return false;

      if (type === "lat") {
        return num >= -90 && num <= 90;
      } else {
        return num >= -180 && num <= 180;
      }
    },
    [],
  );

  // Reverse geocoding function
  const reverseGeocode = useCallback(
    async (lat: number, lng: number, showNotification: boolean = false) => {
      if (!geocoder) return;
      if (isSearching) return;

      try {
        setIsSearching(true);
        const response = await geocoder.geocode({ location: { lat, lng } });

        if (response.results.length > 0) {
          const address = response.results[0].formatted_address;
          setAddressInput(address);
          if (onAddressChange) {
            onAddressChange(address);
          }

          if (showNotification) {
            showToast(
              "Dirección actualizada",
              "Se ha actualizado la dirección automáticamente.",
            );
          }
        }
      } catch (error) {
        console.error("Error in reverse geocoding:", error);
      } finally {
        setIsSearching(false);
      }
    },
    [geocoder, onAddressChange, showToast, isSearching],
  );

  // Handle address search
  const handleAddressSearch = useCallback(async () => {
    if (!geocoder || !addressInput.trim()) return;

    try {
      setIsSearching(true);
      const response = await geocoder.geocode({ address: addressInput });

      if (response.results.length > 0) {
        const location = response.results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        // Set updating flag to prevent useEffect from resetting inputs
        isUpdatingRef.current = true;
        setLatInput(lat.toFixed(6));
        setLngInput(lng.toFixed(6));
        setIsValidLat(true);
        setIsValidLng(true);

        onLocationChange(lat, lng);
        if (onAddressChange) {
          onAddressChange(addressInput);
        }

        if (marker && map) {
          const position = { lat, lng };
          marker.setPosition(position);
          map.panTo(position);
        }

        showToast(
          "Ubicación encontrada",
          "Se ha actualizado la ubicación en el mapa.",
        );

        // Reset updating flag after a longer delay to ensure all updates are complete
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 1000);
      } else {
        showToast(
          "Dirección no encontrada",
          "No se pudo encontrar la dirección especificada.",
          "destructive",
        );
      }
    } catch (error) {
      console.error("Error in geocoding:", error);
      showToast(
        "Error de búsqueda",
        "No se pudo procesar la búsqueda de dirección.",
        "destructive",
      );
    } finally {
      setIsSearching(false);
    }
  }, [
    geocoder,
    addressInput,
    onLocationChange,
    onAddressChange,
    marker,
    map,
    showToast,
  ]);

  // Handle input changes
  const handleLatChange = useCallback(
    (value: string) => {
      setLatInput(value);
      const isValid = validateCoordinate(value, "lat");
      setIsValidLat(isValid);

      if (isValid && !isUpdatingRef.current) {
        // Set updating flag to prevent useEffect from resetting inputs
        isUpdatingRef.current = true;

        const lat = parseFloat(value);
        const lng = parseFloat(lngInput) || longitude;
        onLocationChange(lat, lng);

        if (marker && map) {
          const position = { lat, lng };
          marker.setPosition(position);
          map.panTo(position);
        }

        // Reset updating flag after a longer delay to ensure all updates are complete
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 1000);
      }
    },
    [lngInput, longitude, onLocationChange, marker, map, validateCoordinate],
  );

  const handleLngChange = useCallback(
    (value: string) => {
      setLngInput(value);
      const isValid = validateCoordinate(value, "lng");
      setIsValidLng(isValid);

      if (isValid && !isUpdatingRef.current) {
        // Set updating flag to prevent useEffect from resetting inputs
        isUpdatingRef.current = true;

        const lng = parseFloat(value);
        const lat = parseFloat(latInput) || latitude;
        onLocationChange(lat, lng);

        if (marker && map) {
          const position = { lat, lng };
          marker.setPosition(position);
          map.panTo(position);
        }

        // Reset updating flag after a longer delay to ensure all updates are complete
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 1000);
      }
    },
    [latInput, latitude, onLocationChange, marker, map, validateCoordinate],
  );

  // Handle address input change
  const handleAddressChange = useCallback((value: string) => {
    // When user types, mark as updating so parent prop sync won't override typing
    isUpdatingRef.current = true;
    setAddressInput(value);
  }, []);

  // Handle map click
  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (disabled) return;

      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Set updating flag to prevent useEffect from resetting inputs
        isUpdatingRef.current = true;

        const latStr = lat.toFixed(6);
        const lngStr = lng.toFixed(6);

        setLatInput(latStr);
        setLngInput(lngStr);
        setIsValidLat(true);
        setIsValidLng(true);
        onLocationChange(lat, lng);

        if (marker && map) {
          const position = { lat, lng };
          marker.setPosition(position);
          // Center the map on the new position with smooth animation
          map.panTo(position);
          // Ensure the marker is visible by setting appropriate zoom if needed
          setTimeout(() => {
            const currentZoom = map.getZoom() || 0;
            if (currentZoom < 15) {
              map.setZoom(15);
            }
          }, 300);
        }

        // Perform reverse geocoding to get address
        setTimeout(() => {
          reverseGeocode(lat, lng, true);
        }, 100);

        // Reset updating flag after a longer delay to ensure all updates are complete
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 1000);
      }
    },
    [disabled, onLocationChange, marker, map, reverseGeocode],
  );

  // Handle map load
  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);
      mapRef.current = map;

      // Create marker
      const markerInstance = new google.maps.Marker({
        position: center,
        map: map,
        draggable: !disabled,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," +
            encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#3B82F6"/>
          </svg>
        `),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 24),
        },
      });

      setMarker(markerInstance);
      markerRef.current = markerInstance;

      // Add drag listener
      markerInstance.addListener("dragend", () => {
        const position = markerInstance.getPosition();
        if (position) {
          const lat = position.lat();
          const lng = position.lng();

          // Set updating flag to prevent useEffect from resetting inputs
          isUpdatingRef.current = true;

          const latStr = lat.toFixed(6);
          const lngStr = lng.toFixed(6);

          setLatInput(latStr);
          setLngInput(lngStr);
          onLocationChange(lat, lng);

          // Ensure map stays centered on the marker position with smooth animation
          if (map) {
            map.panTo({ lat, lng });
            // Ensure appropriate zoom level for visibility
            setTimeout(() => {
              const currentZoom = map.getZoom() || 0;
              if (currentZoom < 15) {
                map.setZoom(15);
              }
            }, 300);
          }

          // Perform reverse geocoding when marker is dragged
          setTimeout(() => {
            reverseGeocode(lat, lng, true);
          }, 100);

          // Reset updating flag after a longer delay to ensure all updates are complete
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 1000);
        }
      });
    },
    [center, disabled, onLocationChange, reverseGeocode, map],
  );

  // Handle map unload
  const onMapUnload = useCallback(() => {
    setMap(null);
    setMarker(null);
    mapRef.current = null;
    markerRef.current = null;
  }, []);

  // Update inputs when props change (only if not currently updating and values are different)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const parsedLat = parseFloat(latInput);
      const parsedLng = parseFloat(lngInput);

      const latIsNumber = Number.isFinite(parsedLat);
      const lngIsNumber = Number.isFinite(parsedLng);

      // If user is typing an intermediate non-number (e.g., "-", "", "-1."),
      // skip syncing from props to avoid resetting their input.
      if (!latIsNumber || !lngIsNumber) {
        return;
      }

      // Only update if the values are significantly different (avoid minor floating point differences)
      if (
        Math.abs(parsedLat - latitude) > 0.000001 ||
        Math.abs(parsedLng - longitude) > 0.000001
      ) {
        setLatInput(latitude.toString());
        setLngInput(longitude.toString());
      }
    }
  }, [latitude, longitude, latInput, lngInput]);

  // Update address input when addressValue prop changes (only if not currently updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setAddressInput(addressValue);
    }
  }, [addressValue]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      showToast(
        "Geolocalización no disponible",
        "Tu navegador no soporta geolocalización.",
        "destructive",
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        isUpdatingRef.current = true;
        setLatInput(lat.toFixed(6));
        setLngInput(lng.toFixed(6));
        setIsValidLat(true);
        setIsValidLng(true);
        onLocationChange(lat, lng);

        if (marker && map) {
          const position = { lat, lng };
          marker.setPosition(position);
          map.panTo(position);
        }

        // Perform reverse geocoding to get address
        reverseGeocode(lat, lng, false);

        showToast(
          "Ubicación obtenida",
          "Se ha establecido tu ubicación actual.",
        );

        // Reset updating flag after a longer delay to ensure all updates are complete
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 1000);
      },
      (error) => {
        console.error("Error getting location:", error);
        showToast(
          "Error de geolocalización",
          "No se pudo obtener tu ubicación actual.",
          "destructive",
        );
      },
    );
  }, [onLocationChange, marker, map, reverseGeocode, showToast]);

  return (
    <div className={`space-y-4 md:space-y-5 lg:space-y-6 ${className}`}>
      {/* Address Search Field */}
      {showAddressSearch && (
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
            <Search className="w-4 h-4 text-indigo-600" />
            Buscar dirección{" "}
            {!disabled && <span className="text-xs text-red-500">*</span>}
          </label>
          <div className="relative" style={{ zIndex: 1 }}>
            <Input
              ref={addressInputRef}
              type="text"
              value={addressInput}
              onChange={(e) => handleAddressChange(e.target.value)}
              placeholder={placeholder.address}
              disabled={disabled}
              className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400 pr-12"
              autoComplete="off"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddressSearch}
              disabled={disabled || isSearching || !addressInput.trim()}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Escribe una dirección y presiona el botón de búsqueda o selecciona
            una sugerencia
          </p>
        </div>
      )}

      {/* Input Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-600" />
            Latitud{" "}
            {!disabled && <span className="text-xs text-red-500">*</span>}
          </label>
          <Input
            type="text"
            value={latInput}
            onChange={(e) => handleLatChange(e.target.value)}
            placeholder={placeholder.lat}
            disabled={disabled}
            className={`h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400 ${
              !isValidLat && latInput !== ""
                ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                : ""
            }`}
            style={{ MozAppearance: "textfield" }}
          />
          {!isValidLat && latInput !== "" && (
            <p className="text-red-500 text-xs font-medium mt-1">
              Latitud debe estar entre -90 y 90
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-600" />
            Longitud{" "}
            {!disabled && <span className="text-xs text-red-500">*</span>}
          </label>
          <Input
            type="text"
            value={lngInput}
            onChange={(e) => handleLngChange(e.target.value)}
            placeholder={placeholder.lng}
            disabled={disabled}
            className={`h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400 ${
              !isValidLng && lngInput !== ""
                ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                : ""
            }`}
            style={{ MozAppearance: "textfield" }}
          />
          {!isValidLng && lngInput !== "" && (
            <p className="text-red-500 text-xs font-medium mt-1">
              Longitud debe estar entre -180 y 180
            </p>
          )}
        </div>
      </div>

      {/* Action Button */}
      {!disabled && (
        <div className="flex flex-col items-stretch sm:flex-row sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={getCurrentLocation}
            disabled={disabled}
            className="flex items-center justify-center gap-2 px-4 py-2 md:py-3 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-medium w-full sm:w-auto h-10 md:h-11 lg:h-12"
          >
            <Navigation className="w-4 h-4" />
            Usar ubicación actual
          </Button>
        </div>
      )}

      {/* Map */}
      {!disabled && (
        <div className="space-y-2 md:space-y-3">
          <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
            Selecciona la ubicación en el mapa
          </label>
          <div
            className="relative border border-gray-200 rounded-xl overflow-hidden shadow-sm"
            style={{ height: mapHeight }}
          >
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={15}
              onClick={handleMapClick}
              onLoad={onMapLoad}
              onUnmount={onMapUnload}
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                styles: [
                  {
                    featureType: "all",
                    elementType: "all",
                    stylers: [{ saturation: -20 }, { lightness: 10 }],
                  },
                ],
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Haz clic en el mapa para seleccionar la ubicación o arrastra el
            marcador
          </p>
        </div>
      )}
    </div>
  );
};

export const LocationPicker: React.FC<LocationPickerProps> = (props) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_OPTIONS);

  if (!isLoaded) {
    return (
      <div className="space-y-4 md:space-y-5 lg:space-y-6">
        {/* Address Search Field */}
        {props.showAddressSearch && (
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
              <Search className="w-4 h-4 text-indigo-600" />
              Buscar dirección
            </label>
            <Input
              type="text"
              value={props.addressValue || ""}
              onChange={(e) => {
                if (props.onAddressChange) {
                  props.onAddressChange(e.target.value);
                }
              }}
              placeholder={props.placeholder?.address ?? "Buscar dirección..."}
              disabled={props.disabled}
              className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-indigo-600" />
              Latitud
            </label>
            <Input
              type="text"
              value={props.latitude.toString()}
              onChange={(e) => {
                const lat = parseFloat(e.target.value) || 0;
                const lng = props.longitude;
                props.onLocationChange(lat, lng);
              }}
              placeholder={props.placeholder?.lat ?? "13.6731"}
              disabled={props.disabled}
              className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
              style={{ MozAppearance: "textfield" }}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-indigo-600" />
              Longitud
            </label>
            <Input
              type="text"
              value={props.longitude.toString()}
              onChange={(e) => {
                const lng = parseFloat(e.target.value) || 0;
                const lat = props.latitude;
                props.onLocationChange(lat, lng);
              }}
              placeholder={props.placeholder?.lng ?? "-89.2797"}
              disabled={props.disabled}
              className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
              style={{ MozAppearance: "textfield" }}
            />
          </div>
        </div>

        <div
          className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-gray-50 flex items-center justify-center"
          style={{ height: props.mapHeight || "300px" }}
        >
          <p className="text-gray-500 font-medium">Cargando mapa...</p>
        </div>
      </div>
    );
  }

  return <LocationPickerContent {...props} />;
};
